<template>
  <div>
    <!-- Page Header -->
    <b-card>
      <b-row>
        <b-col
          cols="12"
          md="8"
        >
          <h3 class="mb-1">
            استيراد الطلبات الخارجية (اصلاح زراعي)
          </h3>
          <b-card-text class="mb-0">
            قم بتحميل ملف Excel لاستيراد الطلبات أو الطلبات الخارجية
          </b-card-text>
        </b-col>
      </b-row>
    </b-card>

    <!-- Import Form Card -->
    <b-card class="mt-2">
      <b-card-title>رفع ملف Excel</b-card-title>

      <b-form @submit.prevent="handleImport">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="اختر الملف"
              label-for="file-input"
            >
              <b-form-file
                id="file-input"
                v-model="importFile"
                :state="fileState"
                placeholder="اختر ملف Excel (.xlsx أو .xls)"
                drop-placeholder="اسحب الملف هنا"
                accept=".xlsx,.xls"
                @change="handleFileChange"
              />
              <b-form-invalid-feedback v-if="fileError">
                {{ fileError }}
              </b-form-invalid-feedback>
              <b-form-text v-if="importFile && !fileError">
                الملف المحدد: {{ importFile.name }} ({{ formatFileSize(importFile.size) }})
              </b-form-text>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="نوع الاستيراد"
              label-for="import-type"
            >
              <b-form-select
                id="import-type"
                v-model="importType"
                :options="importTypeOptions"
                :disabled="loading"
              />
              <!-- <b-form-text>
                اختر "تلقائي" للكشف التلقائي من بنية الملف
              </b-form-text> -->

            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <b-button
              type="submit"
              variant="primary"
              :disabled="!importFile || loading || !!fileError"
              class="mr-1"
            >
              <feather-icon
                v-if="!loading"
                icon="UploadIcon"
                class="mr-50"
              />
              <b-spinner
                v-else
                small
                class="mr-50"
              />
              <span v-if="!loading">بدء الاستيراد</span>
              <span v-else>جاري الاستيراد...</span>
            </b-button>

            <b-button
              variant="outline-secondary"
              :disabled="loading"
              @click="resetForm"
            >
              إعادة تعيين
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Results Card -->
    <b-card
      v-if="importResult"
      class="mt-2"
    >
      <b-card-title>
        <feather-icon
          :icon="importResult.success ? 'CheckCircleIcon' : 'AlertCircleIcon'"
          :class="importResult.success ? 'text-success' : 'text-danger'"
          class="mr-50"
        />
        {{ importResult.queued ? 'جاري المعالجة في الخلفية' : 'نتائج الاستيراد' }}
      </b-card-title>

      <b-alert
        v-if="importResult.queued"
        show
        variant="info"
        class="mb-0"
      >
        {{ queuedImportMessage }}
      </b-alert>

      <div v-else-if="importResult.success && importResult.data">
        <!-- Summary Statistics -->
        <b-row class="mb-2">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-primary">
              <h4 class="mb-0">
                {{ importResult.data.total || 0 }}
              </h4>
              <small>إجمالي الصفوف</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-success">
              <h4 class="mb-0">
                {{ importResult.data.imported || 0 }}
              </h4>
              <small>تم الاستيراد</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-info">
              <h4 class="mb-0">
                {{ importResult.data.updated || 0 }}
              </h4>
              <small>تم التحديث</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-warning">
              <h4 class="mb-0">
                {{ importResult.data.skipped || 0 }}
              </h4>
              <small>تم التخطي</small>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="mb-2">
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-danger">
              <h4 class="mb-0">
                {{ importResult.data.errors || 0 }}
              </h4>
              <small>أخطاء</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-secondary">
              <h4 class="mb-0">
                {{ importResult.data.processed || 0 }}
              </h4>
              <small>تم المعالجة</small>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-card class="text-center bg-light-secondary">
              <h4 class="mb-0">
                {{ formatDuration(importResult.data.duration_seconds) }}
              </h4>
              <small>مدة المعالجة</small>
            </b-card>
          </b-col>
        </b-row>

        <!-- Error Details Table -->
        <div v-if="importResult.data.error_details && importResult.data.error_details.length > 0">
          <h5 class="mt-3 mb-2 text-danger">
            تفاصيل الأخطاء
          </h5>
          <b-table
            :items="importResult.data.error_details"
            :fields="errorFields"
            responsive
            striped
            hover
            small
          >
            <template #cell(row)="data">
              <b-badge variant="warning">
                صف {{ data.value }}
              </b-badge>
            </template>
            <template #cell(error)="data">
              <span class="text-danger">{{ data.value }}</span>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BCardTitle,
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormFile,
  BFormSelect,
  BFormText,
  BFormInvalidFeedback,
  BTable,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import externalRequestsAPI from '@/api/external-requests/externalRequests'

export default {
  name: 'ExternalOrdersImport',
  components: {
    BCard,
    BCardText,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormFile,
    BFormSelect,
    BFormText,
    BFormInvalidFeedback,
    BTable,
    BBadge,
    BSpinner,
  },
  data() {
    return {
      importFile: null,
      importType: 'fees',
      loading: false,
      fileError: null,
      importResult: null,
      importTypeOptions: [
        // { value: 'auto', text: 'تلقائي (Auto-detect)' },
        { value: 'requests', text: 'طلبات (Requests)' },
        { value: 'orders', text: 'طلبات (Orders)' },
        { value: 'coordinates', text: 'الإحداثيات (Coordinates)' },
        { value: 'fees', text: 'الرسوم (Fees)' },
        { value: 'criminal_reports', text: 'المحضر الجنائي (Criminal Reports)' },
      ],
      errorFields: [
        { key: 'row', label: 'رقم الصف', sortable: true },
        { key: 'error', label: 'الخطأ', sortable: false },
      ],
    }
  },
  computed: {
    fileState() {
      if (this.fileError) return false
      if (this.importFile) return true
      return null
    },
    queuedImportMessage() {
      const data = this.importResult && this.importResult.data
      return (data && data.message) || 'تم بدء الاستيراد في الخلفية. سيتم إشعارك عند اكتمال العملية.'
    },
  },
  methods: {
    handleFileChange(event) {
      this.fileError = null
      this.importResult = null

      const file = event.target.files[0]
      if (!file) {
        this.importFile = null
        return
      }

      // Validate file type
      const validExtensions = ['.xlsx', '.xls']
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`

      if (!validExtensions.includes(fileExtension)) {
        this.fileError = 'يجب أن يكون الملف من نوع Excel (.xlsx أو .xls)'
        this.importFile = null
        return
      }

      const maxSize = 150 * 1024 * 1024
      if (file.size > maxSize) {
        this.fileError = 'حجم الملف يجب أن يكون أقل من 150 ميجابايت'
        this.importFile = null
        return
      }

      this.importFile = file
    },

    async handleImport() {
      if (!this.importFile || this.fileError) {
        this.showError('يرجى اختيار ملف صحيح')
        return
      }

      this.loading = true
      this.importResult = null

      try {
        const formData = new FormData()
        formData.append('file', this.importFile)

        // Only append type if not 'auto'
        if (this.importType !== 'auto') {
          formData.append('type', this.importType)
        }

        const response = await externalRequestsAPI.import(formData)
        const responseData = response.data || response

        if (responseData.queued) {
          this.importResult = {
            success: true,
            queued: true,
            data: responseData,
          }
          const message = responseData.message
            || response.message
            || 'تم بدء الاستيراد في الخلفية. سيتم إشعارك عند الانتهاء.'
          this.showSuccess(message)
          return
        }

        this.importResult = {
          success: response.success !== false,
          data: responseData,
        }

        const importedCount = responseData.imported || 0
        const message = response.message || `تم الاستيراد بنجاح! تم استيراد ${importedCount} سجل جديد`
        this.showSuccess(message)
      } catch (error) {
        this.importResult = {
          success: false,
          error: error.response?.data?.message || error.message || 'حدث خطأ أثناء الاستيراد',
        }

        const errorMessage = error.response?.data?.message
                           || error.response?.data?.errors?.file?.[0]
                           || 'حدث خطأ أثناء الاستيراد'
        this.showError(errorMessage)

        // If there's error data in response, show it
        if (error.response?.data?.data) {
          this.importResult.data = error.response.data.data
        }
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.importFile = null
      this.importType = 'auto'
      this.fileError = null
      this.importResult = null
      // Reset file input
      const fileInput = document.getElementById('file-input')
      if (fileInput) {
        fileInput.value = ''
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
    },

    formatDuration(seconds) {
      if (!seconds) return '0 ثانية'
      if (seconds < 60) {
        return `${Math.round(seconds * 10) / 10} ثانية`
      }
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.round(seconds % 60)
      return `${minutes} دقيقة ${remainingSeconds} ثانية`
    },

    showSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'نجاح',
          icon: 'CheckCircleIcon',
          variant: 'success',
          text: message,
        },
      })
    },

    showError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'خطأ',
          icon: 'AlertCircleIcon',
          variant: 'danger',
          text: message,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.1) !important;
}

.bg-light-success {
  background-color: rgba(40, 199, 111, 0.1) !important;
}

.bg-light-info {
  background-color: rgba(0, 207, 232, 0.1) !important;
}

.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.1) !important;
}

.bg-light-danger {
  background-color: rgba(234, 84, 85, 0.1) !important;
}

.bg-light-secondary {
  background-color: rgba(168, 170, 174, 0.1) !important;
}
</style>
